<script setup lang="ts">
import { NavigationMenuItem, NavigationMenuTrigger, NavigationMenuContent } from '@/components/navigation-menu'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { useUserStore } from '@/authentication/user.store'
import { sendToLMS } from '@/authentication/api_js'
import { toolkitUrls, goalbookUrls } from '@/util/links'
import { MENU_ITEM_TYPES, MenuItem, isMenuItemVisible } from './nav_types.ts'
import NavSubmenu from './NavSubmenu.vue'
import GbDialog from '@/components/GbDialog.vue'

const props = defineProps({
  navigationMenuItemStyle: {
    type: String,
    default: ''
  },
  navigationMenuContentStyle: {
    type: String,
    default: ''
  },
  isHamburger: {
    type: Boolean,
    default: false
  },
})

const { showTrainingButton } = storeToRefs(useUserStore())
const analytics = getAnalyticsTracker()
const visibilityStore = useVisibilityStore()
const userStore = useUserStore()
const { iodVisibility, betaVisibility, effectsVisibility, aiFeatureVisibility, nycWebinarsVisibility } = storeToRefs(visibilityStore)
const showLmsErrorDialog = ref(false)
// Track which mobile menus are open
const openMenus = ref({
  instructionalDesign: false,
  yourContent: false,
  trainingCenter: false,
  admin: false
})

const showAdminMenu = computed(() => {
  return visibilityStore.editorVisibility
})

const impactOfDisabilityNavbarUrl = computed(() => {
  const url = new URL(toolkitUrls.impactOfDisability(aiFeatureVisibility.value, betaVisibility.value))
  url.searchParams.set('ref', 'navbar')
  return url.toString()
})

function toggleMobileMenu(menuName: string) {
  openMenus.value[menuName as keyof typeof openMenus.value] = !openMenus.value[menuName as keyof typeof openMenus.value]
}

const instructionalDesignMenuItems = computed(() => [
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Present Levels Wizard',
    href: (iodVisibility.value || effectsVisibility.value) && aiFeatureVisibility.value
      ? `${goalbookUrls.toolkit}/v/presentlevels/v2/session?ref=navbar`
      : `${toolkitUrls.presentlevelsWizard}/?ref=navbar`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Compose Impact of Disability',
    href: impactOfDisabilityNavbarUrl.value,
    condition: iodVisibility.value
  },
  { type: MENU_ITEM_TYPES.DIVIDER },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Assessment Wizard',
    href: `${toolkitUrls.assessmentWizard}/?ref=navbar`,
    condition: aiFeatureVisibility.value
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Browse Assessments',
    href: `${toolkitUrls.browseAssessments}/?ref=navbar`
  },
  { type: MENU_ITEM_TYPES.DIVIDER },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Learning Goal Wizard',
    href: `${toolkitUrls.goalWizard}/?ref=navbar`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Browse Learning Goals',
    href: `${toolkitUrls.browse}/?ref=navbar`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Learning Goal Index',
    href: `${toolkitUrls.home}/goal-index?ref=navbar`
  },
  { type: MENU_ITEM_TYPES.DIVIDER },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Strategy Wizard',
    href: `${toolkitUrls.strategiesWizard}/?ref=navbar`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Browse Strategies',
    href: `${toolkitUrls.browseStrategies}?ref=navbar`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'UDL Strategy Index',
    href: `${toolkitUrls.home}/strategies?ref=navbar`
  }
])

const yourContentMenuItems = computed<MenuItem[]>(() => [
  {
    type: MENU_ITEM_TYPES.ROUTER_LINK,
    text: 'All Folders',
    to: betaVisibility.value ? { path: '/library/all', query: { beta: 'true' } } : { path: '/library/all' },
    event: 'Clicked Library',
    eventParams: { ref: 'navbar' }
  },
  { type: MENU_ITEM_TYPES.DIVIDER },
  {
    type: MENU_ITEM_TYPES.ROUTER_LINK,
    text: 'My Folders',
    to: '/library/owned',
    event: 'Clicked My Folders',
    eventParams: { ref: 'navbar' }
  },
  {
    type: MENU_ITEM_TYPES.ROUTER_LINK,
    text: 'Shared With Me',
    to: '/library/shared',
    event: 'Clicked Shared With Me',
    eventParams: { ref: 'navbar' }
  },
  { type: MENU_ITEM_TYPES.DIVIDER },
  {
    type: MENU_ITEM_TYPES.ROUTER_LINK,
    text: 'Recently Saved Content',
    to: '/library/recently_saved',
    event: 'Clicked Recently Saved',
    eventParams: { ref: 'navbar' }
  },
  {
    type: MENU_ITEM_TYPES.ROUTER_LINK,
    text: 'Recently Viewed Content',
    to: '/library/recently_viewed/goals',
    event: 'Clicked Recently Viewed',
    eventParams: { ref: 'navbar' }
  }
])

const trainingCenterMenuItems = computed<MenuItem[]>(() => [
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Upcoming Webinars',
    href: nycWebinarsVisibility.value
      ? goalbookUrls.nycWebinars
      : `${goalbookUrls.upcomingWebinars}`,
    condition: aiFeatureVisibility.value || nycWebinarsVisibility.value,
    event: 'Clicked Upcoming Webinars',
    eventParams: { ref: 'navbar' },
    target: '_blank',
    external: true
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'On-Demand Courses',
    href: '#',
    onClick: handleLMSClick,
    condition: (aiFeatureVisibility.value || nycWebinarsVisibility.value) && showTrainingButton.value,
    external: true
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Recent Updates',
    href: `${goalbookUrls.recentUpdates}`,
    event: 'Clicked View All Posts',
    eventParams: { ref: 'navbar' },
    target: '_blank',
    external: true,
    condition: aiFeatureVisibility.value
  }
])

const hasTrainingCenterItems = computed(() =>
  trainingCenterMenuItems.value.some(isMenuItemVisible)
)

const adminMenuItems = computed<MenuItem[]>(() => [
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'UDL Strategies Admin',
    href: `${goalbookUrls.toolkit}/strategies/list`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Goals Admin',
    href: `${goalbookUrls.toolkit}/filter`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Research Citations',
    href: `${goalbookUrls.toolkit}/research/list`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'My Goals',
    href: `${goalbookUrls.toolkit}/filter/#?author_id=${userStore.userId}`
  },
  { type: MENU_ITEM_TYPES.DIVIDER },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Admin Standards',
    href: `${goalbookUrls.pathways}/admin/standards`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'UDL Standards',
    href: `${goalbookUrls.toolkit}/standards/udl`
  },
  { type: MENU_ITEM_TYPES.DIVIDER },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'New Anchor with Access Points',
    href: `${goalbookUrls.toolkit}/v/admin/anchor-page/new`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Add Goal',
    href: `${goalbookUrls.toolkit}/add/goal`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Add Strategy',
    href: `${goalbookUrls.toolkit}/add/strategy`
  },
  {
    type: MENU_ITEM_TYPES.LINK,
    text: 'Add Research Citation',
    href: `${goalbookUrls.toolkit}/add/research`
  }
])

async function handleLMSClick() {
  try {
    analytics.sendEvent('Clicked On-Demand Courses', { ref: 'navbar' })
    await sendToLMS()
    analytics.sendEvent('Logged Into On Demand Training')
  } catch (err) {
    analytics.sendEvent('On Demand Training Login Failed')
    const errorMessage = err instanceof Error ? err.message : String(err)
    console.error('caught error from sendToLMS: ' + errorMessage)
    showLmsErrorDialog.value = true
  }
}
</script>

<template>
  <!-- Instructional Design Tools Menu -->
  <template v-if="isHamburger">
    <div class="mobile-menu-item">
      <button aria-label="Instructional Design Tools Menu" class="mobile-menu-trigger" @click="toggleMobileMenu('instructionalDesign')">
        <span>Instructional Design Tools</span>
        <span class="material-icons-round text-text-2xl" aria-hidden="true">{{ openMenus.instructionalDesign ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
      </button>
      <div v-show="openMenus.instructionalDesign" class="bg-white text-gray-800 w-full">
        <NavSubmenu :menu-items="instructionalDesignMenuItems" :is-mobile="true" />
      </div>
    </div>
  </template>
  <NavigationMenuItem v-else>
    <NavigationMenuTrigger aria-label="Instructional Design Tools Menu">
      Instructional Design Tools
    </NavigationMenuTrigger>
    <NavigationMenuContent as="nav" align="start">
      <NavSubmenu :menu-items="instructionalDesignMenuItems" />
    </NavigationMenuContent>
  </NavigationMenuItem>

  <!-- My Library Menu -->
  <template v-if="isHamburger">
    <div class="mobile-menu-item">
      <button aria-label="My Library Menu" class="mobile-menu-trigger" @click="toggleMobileMenu('yourContent')">
        <span>My Library</span>
        <span class="material-icons-round text-text-2xl" aria-hidden="true">{{ openMenus.yourContent ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
      </button>
      <div v-show="openMenus.yourContent" class="bg-white text-gray-800 w-full">
        <NavSubmenu :menu-items="yourContentMenuItems" :is-mobile="true" />
      </div>
    </div>
  </template>
  <NavigationMenuItem v-else>
    <NavigationMenuTrigger aria-label="My Library Menu">
      My Library
    </NavigationMenuTrigger>
    <NavigationMenuContent align="start" class="w-[250px]">
      <NavSubmenu :menu-items="yourContentMenuItems" />
    </NavigationMenuContent>
  </NavigationMenuItem>

  <!-- Training Center Menu -->
  <template v-if="hasTrainingCenterItems">
    <template v-if="isHamburger">
      <div class="mobile-menu-item">
        <button aria-label="Training Center Menu" class="mobile-menu-trigger" @click="toggleMobileMenu('trainingCenter')">
          <span>Training Center</span>
          <span class="material-icons-round text-text-2xl" aria-hidden="true">{{ openMenus.trainingCenter ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
        </button>
        <div v-show="openMenus.trainingCenter" class="bg-white text-gray-800 w-full">
          <NavSubmenu :menu-items="trainingCenterMenuItems" :is-mobile="true" />
        </div>
      </div>
    </template>
    <NavigationMenuItem v-else :class="[navigationMenuItemStyle, 'h-full']">
      <NavigationMenuTrigger aria-label="Training Center Menu">
        Training Center
      </NavigationMenuTrigger>
      <NavigationMenuContent align="start" :class="[navigationMenuContentStyle, 'w-[200px]']">
        <NavSubmenu :menu-items="trainingCenterMenuItems" />
      </NavigationMenuContent>
    </NavigationMenuItem>
  </template>

  <!-- Admin Menu -->
  <template v-if="isHamburger && showAdminMenu">
    <div class="mobile-menu-item">
      <button aria-label="Admin Menu" class="mobile-menu-trigger" @click="toggleMobileMenu('admin')">
        <span>Admin</span>
        <span class="material-icons-round text-text-2xl" aria-hidden="true">{{ openMenus.admin ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
      </button>
      <div v-show="openMenus.admin" class="bg-white text-gray-800 w-full mobile-menu-content">
        <NavSubmenu :menu-items="adminMenuItems" :is-mobile="true" />
      </div>
    </div>
  </template>
  <NavigationMenuItem v-if="!isHamburger && showAdminMenu" :class="[navigationMenuItemStyle, 'h-full']">
    <NavigationMenuTrigger aria-label="Admin Menu">
      Admin
    </NavigationMenuTrigger>
    <NavigationMenuContent align="start">
      <NavSubmenu :menu-items="adminMenuItems" />
    </NavigationMenuContent>
  </NavigationMenuItem>

  <GbDialog 
    dialogId="lms-error-dialog"
    v-model:visible="showLmsErrorDialog"
    header="Sorry! An error has occurred."
    :footerButtons="true"
  >
    <template #bodyContent>
      <p class="mt-2">An error has occurred accessing the On Demand Training. Our Support team has been notified of the issue. To get training now, check out our webinars
        <a 
          :href="nycWebinarsVisibility ? goalbookUrls.nycWebinars : goalbookUrls.upcomingWebinars"
          target="_blank" 
          class="text-blue-600 hover:underline"
        >
          here</a>.
      </p>
    </template>
  </GbDialog>
</template>